import { useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft, CheckCircle2, MoreHorizontal, Plus } from 'lucide-react';
import { Button } from '../../components/Button';
import { Card, CardHeader } from '../../components/Card';
import { Badge } from '../../components/Badge';
import { Banner } from '../../components/Banner';
import { Input } from '../../components/Input';
import { Tabs } from '../../components/Tabs';
import { Drawer } from '../../components/Drawer';
import { Money } from '../../components/Money';
import { useAdminPackage, updateAdminPackage, type AdminPackageDetail } from '../../api/admin';

const EDITOR_STATUS_TONE: Record<string, 'success' | 'info' | 'warning' | 'neutral'> = {
  published: 'success', draft: 'info', deprecated: 'warning', archived: 'neutral',
};
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** Package editor — General + Prices live; Conditions/Modules/Add-ons/Availability/Tax still mock. */
export function PackageEditor() {
  const { id } = useParams({ strict: false }) as { id?: string };
  const { data: detail, isLoading } = useAdminPackage(id ?? '');

  if (isLoading || !detail) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-[1440px] mx-auto space-y-4" aria-busy="true">
        <div className="skeleton h-8 w-64 max-w-full rounded" />
        <div className="space-y-3"><div className="skeleton h-14 rounded-lg" /><div className="skeleton h-14 rounded-lg" /><div className="skeleton h-14 rounded-lg" /></div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1440px] mx-auto">
      <Link to="/admin/packages" className="inline-flex items-center gap-1.5 text-sm text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 mb-4 transition">
        <ArrowLeft className="w-4 h-4" />
        All packages
      </Link>

      <Card className="p-5 sm:p-6 mb-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <div className="text-xs font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-400 mb-1">
              Edit package · {detail.ownedBy === 'platform' ? 'Platform-owned' : 'Reseller-owned'}
            </div>
            <h1 className="text-2xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50 flex items-center gap-3">
              {detail.name}
              <Badge tone={EDITOR_STATUS_TONE[detail.status] ?? 'neutral'}>{cap(detail.status)}</Badge>
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm"><MoreHorizontal className="w-4 h-4" /></Button>
          </div>
        </div>
      </Card>

      <Card className="px-5 sm:px-6 pt-2 pb-5 sm:pb-6">
        <Tabs
          tabs={[
            { id: 'general', label: 'General', content: <GeneralTab id={detail.id} detail={detail} /> },
            { id: 'prices', label: 'Prices', content: <PricesTab prices={detail.prices} />, count: detail.prices.length },
            { id: 'conditions', label: 'Conditions', content: <ConditionsTab id={detail.id} detail={detail} /> },
            { id: 'modules', label: 'Modules', content: <ModulesTab id={detail.id} detail={detail} /> },
            { id: 'addons', label: 'Add-ons', content: <AddonsTab /> },
            { id: 'availability', label: 'Availability', content: <AvailabilityTab /> },
            { id: 'tax', label: 'Tax', content: <TaxTab /> },
          ]}
        />
      </Card>
    </div>
  );
}

function GeneralTab({ id, detail }: { id: string; detail: AdminPackageDetail }) {
  const qc = useQueryClient();
  const [name, setName] = useState(detail.name);
  const code = detail.internalCode;
  const [tagline, setTagline] = useState(detail.tagline);
  const [description, setDescription] = useState(detail.description);
  const [badge, setBadge] = useState(detail.marketingBadge);
  const [status, setStatus] = useState<'published' | 'draft' | 'deprecated' | 'archived'>(detail.status);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    if (!name.trim()) { setError('Name cannot be empty.'); return; }
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await updateAdminPackage(id, { name: name.trim(), tagline, description, marketingBadge: badge, status });
      await qc.invalidateQueries({ queryKey: ['admin', 'package', id] });
      await qc.invalidateQueries({ queryKey: ['admin', 'allPackages'] });
      await qc.invalidateQueries({ queryKey: ['packages'] });
      setSaved(true);
    } catch {
      setError('Could not save changes.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-5 max-w-2xl">
      {error && (
        <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 rounded-md px-3 py-2">{error}</div>
      )}
      <Input label="Name" required value={name} onChange={(e) => { setName(e.target.value); setSaved(false); }} />
      <Input label="Internal code" value={code} disabled hint="Immutable; used by webhooks" />
      <Input label="Tagline" value={tagline} onChange={(e) => setTagline(e.target.value)} />
      <div>
        <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">Description</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          className="block w-full px-3 py-2.5 text-sm rounded-md bg-white dark:bg-zinc-900 border border-zinc-300 dark:border-zinc-700 focus:outline-none focus:ring-2 focus:ring-brand resize-none"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">Marketing badge</label>
        <select
          value={badge}
          onChange={(e) => setBadge(e.target.value)}
          className="block w-full px-3 py-2.5 text-sm rounded-md bg-white dark:bg-zinc-900 border border-zinc-300 dark:border-zinc-700 focus:outline-none focus:ring-2 focus:ring-brand"
        >
          <option value="">— None —</option>
          <option value="most-popular">Most popular</option>
          <option value="best-value">Best value</option>
          <option value="new">New</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">Status</label>
        <div className="space-y-2">
          {(['published', 'draft', 'deprecated', 'archived'] as const).map((s) => (
            <label key={s} className="flex items-center gap-2.5 text-sm text-zinc-700 dark:text-zinc-200 cursor-pointer">
              <input
                type="radio"
                name="status"
                checked={status === s}
                onChange={() => setStatus(s)}
                className="text-brand focus:ring-brand"
              />
              <span className="capitalize">{s}</span>
            </label>
          ))}
        </div>
      </div>

      <Banner tone="warning">
        Changing status from <strong>Published</strong> to <strong>Deprecated</strong> blocks new sign-ups for this package.
        Existing subscribers are unaffected.
      </Banner>

      <div className="pt-3 border-t border-zinc-200 dark:border-zinc-700 flex items-center justify-end gap-3">
        {saved && (
          <span className="text-sm text-emerald-600 dark:text-emerald-400 inline-flex items-center gap-1.5">
            <CheckCircle2 className="w-4 h-4" /> Saved
          </span>
        )}
        <Button onClick={save} loading={saving} disabled={saving}>Save changes</Button>
      </div>
    </div>
  );
}

function PricesTab({ prices }: { prices: AdminPackageDetail['prices'] }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
          Prices <span className="text-zinc-500 dark:text-zinc-400 tabular-nums">({prices.length})</span>
        </h3>
        <Button size="sm" iconLeft={<Plus className="w-4 h-4" />} onClick={() => setDrawerOpen(true)}>Add price</Button>
      </div>

      <div className="overflow-x-auto nice-scroll -mx-6">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 bg-zinc-50/50 dark:bg-zinc-900/50">
            <tr>
              <th className="text-left font-medium px-6 py-3">Currency</th>
              <th className="text-left font-medium px-4 py-3">Interval</th>
              <th className="text-right font-medium px-4 py-3">Amount</th>
              <th className="text-right font-medium px-4 py-3">Trial</th>
              <th className="text-left font-medium px-4 py-3">Status</th>
              <th className="text-right font-medium px-6 py-3">On subs</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
            {prices.length === 0 && (
              <tr><td colSpan={6} className="px-6 py-8 text-center text-sm text-zinc-500 dark:text-zinc-400">No prices yet.</td></tr>
            )}
            {prices.map((p) => (
              <tr key={p.id} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/70 transition">
                <td className="px-6 py-3 font-medium text-zinc-950 dark:text-zinc-50">{p.currency}</td>
                <td className="px-4 py-3 text-zinc-700 dark:text-zinc-200 capitalize">{p.interval === 'month' ? 'Monthly' : p.interval === 'year' ? 'Yearly' : p.interval}</td>
                <td className="px-4 py-3 text-right">
                  <Money minor={p.amount} currencyCode={p.currency} hideCents tone="neutral" className="text-zinc-950 dark:text-zinc-50" />
                </td>
                <td className="px-4 py-3 text-right tabular-nums text-zinc-700 dark:text-zinc-200">{p.trialDays} d</td>
                <td className="px-4 py-3">
                  <Badge tone={p.status === 'active' ? 'success' : p.status === 'draft' ? 'info' : 'neutral'}>
                    {p.status.charAt(0).toUpperCase() + p.status.slice(1)}
                  </Badge>
                </td>
                <td className="px-6 py-3 text-right tabular-nums text-zinc-700 dark:text-zinc-200">{p.subscribersOn}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Banner tone="info" className="mt-5">
        Existing subscribers stay on their current price unless you migrate them.
        <Link to="/admin/packages-migrate" className="ml-1 underline font-medium">Schedule a price change</Link>.
      </Banner>

      <AddPriceDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
    </div>
  );
}

function AddPriceDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [interval, setInterval] = useState<'month' | 'year' | 'quarter' | 'one_time'>('month');
  const [proration, setProration] = useState<'prorate' | 'full'>('prorate');
  const [anchor, setAnchor] = useState<'start' | 'first' | 'custom'>('start');

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Add a new price"
      width="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={onClose}>Save price</Button>
        </>
      }
    >
      <div className="space-y-5">
        <div>
          <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">Currency</label>
          <select className="block w-full px-3 py-2.5 text-sm rounded-md bg-white dark:bg-zinc-900 border border-zinc-300 dark:border-zinc-700">
            <option>BDT</option>
            <option>USD</option>
          </select>
        </div>
        <div>
          <span className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">Interval</span>
          <div className="grid grid-cols-2 gap-2">
            {(['month', 'year', 'quarter', 'one_time'] as const).map((i) => (
              <label key={i} className="flex items-center gap-2 text-sm cursor-pointer p-2 rounded-md border border-zinc-200 dark:border-zinc-600 hover:bg-zinc-50 dark:hover:bg-zinc-800">
                <input type="radio" name="interval" checked={interval === i} onChange={() => setInterval(i)} className="text-brand focus:ring-brand" />
                <span className="capitalize">{i.replace('_', '-')}</span>
              </label>
            ))}
          </div>
        </div>
        <Input label="Amount" required defaultValue="1500" />
        <Input label="Trial" defaultValue="7" suffix={<span className="text-xs">days</span>} hint="0 = no trial" />

        <div>
          <span className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">Billing cycle anchor</span>
          <div className="space-y-2">
            <Radio label="Subscription start date" checked={anchor === 'start'} onChange={() => setAnchor('start')} name="anchor" />
            <Radio label="1st of month" checked={anchor === 'first'} onChange={() => setAnchor('first')} name="anchor" />
            <Radio label="Custom day" checked={anchor === 'custom'} onChange={() => setAnchor('custom')} name="anchor" />
          </div>
        </div>

        <div>
          <span className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">Proration</span>
          <div className="space-y-2">
            <Radio label="Prorate when changing plans" checked={proration === 'prorate'} onChange={() => setProration('prorate')} name="proration" />
            <Radio label="Charge full new price; ignore unused" checked={proration === 'full'} onChange={() => setProration('full')} name="proration" />
          </div>
        </div>

        <div>
          <span className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">Status</span>
          <div className="space-y-2">
            <Radio label="Draft" checked name="price-status" onChange={() => {}} />
            <Radio label="Active" checked={false} name="price-status" onChange={() => {}} />
          </div>
        </div>
      </div>
    </Drawer>
  );
}

function ConditionsTab({ id, detail }: { id: string; detail: AdminPackageDetail }) {
  const qc = useQueryClient();
  const limStr = (v: number | 'unlimited') => (v === 'unlimited' ? '' : String(v));
  const [locations, setLocations] = useState(limStr(detail.limits.locations));
  const [users, setUsers] = useState(limStr(detail.limits.users));
  const [products, setProducts] = useState(limStr(detail.limits.products));
  const [invoices, setInvoices] = useState(limStr(detail.limits.invoices));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toLimit = (s: string): number | 'unlimited' => (s.trim() === '' ? 'unlimited' : Number(s));

  async function save() {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await updateAdminPackage(id, {
        limits: { locations: toLimit(locations), users: toLimit(users), products: toLimit(products), invoices: toLimit(invoices) },
      });
      await qc.invalidateQueries({ queryKey: ['admin', 'package', id] });
      await qc.invalidateQueries({ queryKey: ['admin', 'allPackages'] });
      setSaved(true);
    } catch {
      setError('Could not save the limits.');
    } finally {
      setSaving(false);
    }
  }

  const set = (fn: (v: string) => void) => (v: string) => { fn(v); setSaved(false); };

  return (
    <div className="space-y-6 max-w-2xl">
      {error && (
        <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 rounded-md px-3 py-2">{error}</div>
      )}
      <section>
        <h3 className="text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1">Resource limits enforced on the POS</h3>
        <p className="text-xs text-zinc-500 dark:text-zinc-400 mb-3">Leave a field blank for unlimited.</p>
        <div className="space-y-3">
          <LimitRow label="Locations" value={locations} onChange={set(setLocations)} />
          <LimitRow label="Users" value={users} onChange={set(setUsers)} />
          <LimitRow label="Products" value={products} onChange={set(setProducts)} />
          <LimitRow label="Invoices / month" value={invoices} onChange={set(setInvoices)} />
        </div>
      </section>

      <div className="pt-3 border-t border-zinc-200 dark:border-zinc-700 flex items-center justify-end gap-3">
        {saved && (
          <span className="text-sm text-emerald-600 dark:text-emerald-400 inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> Saved</span>
        )}
        <Button onClick={save} loading={saving} disabled={saving}>Save changes</Button>
      </div>
    </div>
  );
}

function LimitRow({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div className="grid sm:grid-cols-[1fr_160px] gap-3 items-center">
      <label className="text-sm text-zinc-700 dark:text-zinc-200">{label}</label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Unlimited"
        className="px-3 py-2 text-sm rounded-md bg-white dark:bg-zinc-900 border border-zinc-300 dark:border-zinc-700 tabular-nums"
      />
    </div>
  );
}

const MODULE_GROUPS: { title: string; modules: string[] }[] = [
  { title: 'Core', modules: ['POS sale', 'Purchases', 'Inventory', 'Stock transfers', 'Stock adjustment', 'Income', 'Expenses', 'Account'] },
  { title: 'Modules', modules: ['Subscription', 'Services', 'Service staff', 'Types of service', 'Manufacturing', 'Tables', 'Modifiers', 'Booking', 'Kitchen', 'WooCommerce', 'Essentials/HRM', 'CRM', 'Repair', 'RMA', 'Support', 'Superadmin'] },
];

function ModulesTab({ id, detail }: { id: string; detail: AdminPackageDetail }) {
  const qc = useQueryClient();
  const [enabled, setEnabled] = useState<Set<string>>(() => new Set(detail.modules));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function toggle(m: string) {
    setEnabled((prev) => {
      const next = new Set(prev);
      if (next.has(m)) next.delete(m); else next.add(m);
      return next;
    });
    setSaved(false);
  }

  async function save() {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await updateAdminPackage(id, { modules: [...enabled] });
      await qc.invalidateQueries({ queryKey: ['admin', 'package', id] });
      setSaved(true);
    } catch {
      setError('Could not save the modules.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6 max-w-3xl">
      {error && (
        <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 rounded-md px-3 py-2">{error}</div>
      )}
      {MODULE_GROUPS.map((g) => (
        <section key={g.title}>
          <h3 className="text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-3">{g.title}</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
            {g.modules.map((m) => (
              <label key={m} className="flex items-center gap-2.5 text-sm text-zinc-700 dark:text-zinc-200 cursor-pointer">
                <input type="checkbox" checked={enabled.has(m)} onChange={() => toggle(m)} className="rounded border-zinc-300 dark:border-zinc-600 text-brand focus:ring-brand" />
                {m}
              </label>
            ))}
          </div>
        </section>
      ))}
      <div className="pt-3 border-t border-zinc-200 dark:border-zinc-700 flex items-center justify-end gap-3">
        {saved && (
          <span className="text-sm text-emerald-600 dark:text-emerald-400 inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> Saved</span>
        )}
        <Button onClick={save} loading={saving} disabled={saving}>Save changes</Button>
      </div>
    </div>
  );
}

function AddonsTab() {
  return (
    <div className="space-y-3 max-w-2xl">
      <p className="text-sm text-zinc-600 dark:text-zinc-300">
        Choose which optional add-ons subscribers can purchase on top of this package.
      </p>
      {[
        { name: 'Extra location', price: 100, unit: '/loc/mo' },
        { name: 'Extra user', price: 50, unit: '/user/mo' },
        { name: 'Extra products (1k)', price: 200, unit: '/1k/mo' },
        { name: 'Priority support', price: 1000, unit: '/mo' },
        { name: 'Onboarding', price: 5000, unit: 'one-time' },
      ].map((a) => (
        <Card key={a.name} className="p-4 border-zinc-200 dark:border-zinc-700 flex items-center justify-between">
          <label className="flex items-center gap-3 cursor-pointer flex-1">
            <input type="checkbox" defaultChecked={a.name !== 'Onboarding'} className="rounded border-zinc-300 dark:border-zinc-600 text-brand focus:ring-brand" />
            <div>
              <div className="text-sm font-medium text-zinc-950 dark:text-zinc-50">{a.name}</div>
              <div className="text-xs text-zinc-500 dark:text-zinc-400">{a.unit}</div>
            </div>
          </label>
          <Money minor={a.price} hideCents tone="neutral" className="text-sm font-medium text-zinc-950 dark:text-zinc-50" />
        </Card>
      ))}
      <div className="pt-3 border-t border-zinc-200 dark:border-zinc-700 flex justify-end">
        <Button>Save changes</Button>
      </div>
    </div>
  );
}

function AvailabilityTab() {
  const [vis, setVis] = useState<'public' | 'private' | 'internal'>('public');
  const [sold, setSold] = useState<'all' | 'specific' | 'direct'>('all');
  return (
    <div className="space-y-6 max-w-2xl">
      <section>
        <h3 className="text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-3">Visibility</h3>
        <div className="space-y-2">
          <Radio name="vis" checked={vis === 'public'} onChange={() => setVis('public')} label="Public — shown on pricing page" />
          <Radio name="vis" checked={vis === 'private'} onChange={() => setVis('private')} label="Private — direct link only" />
          <Radio name="vis" checked={vis === 'internal'} onChange={() => setVis('internal')} label="Internal — platform staff only" />
        </div>
      </section>

      <section>
        <h3 className="text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-3">Sold by</h3>
        <div className="space-y-2">
          <Radio name="sold" checked={sold === 'all'} onChange={() => setSold('all')} label="All resellers + direct" />
          <Radio name="sold" checked={sold === 'specific'} onChange={() => setSold('specific')} label="Specific resellers" />
          <Radio name="sold" checked={sold === 'direct'} onChange={() => setSold('direct')} label="Direct only (saas.itcitysoft.com)" />
        </div>
        {sold === 'specific' && (
          <div className="mt-3 p-3 rounded-lg bg-zinc-50 dark:bg-zinc-800/40 border border-zinc-200 dark:border-zinc-700">
            <span className="text-xs text-zinc-500 dark:text-zinc-400">Reseller whitelist</span>
            <div className="mt-2 flex flex-wrap gap-1.5">
              <ResellerPill name="Acme Resellers" />
              <ResellerPill name="Sundarban Tech" />
              <button className="px-2 py-1 rounded-md text-xs border border-dashed border-zinc-300 dark:border-zinc-700 text-zinc-500 dark:text-zinc-400 hover:border-zinc-400 dark:hover:border-zinc-600">+ Add reseller</button>
            </div>
          </div>
        )}
      </section>

      <section>
        <h3 className="text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-3">Effective dates</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <Input label="Available from" type="date" defaultValue="2026-05-03" />
          <Input label="Available until" type="date" hint="Leave blank for open-ended" />
        </div>
      </section>

      <div className="pt-3 border-t border-zinc-200 dark:border-zinc-700 flex justify-end">
        <Button>Save changes</Button>
      </div>
    </div>
  );
}

function ResellerPill({ name }: { name: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs bg-white dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-700">
      {name}
      <button className="text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200">×</button>
    </span>
  );
}

type TaxRegion = { id: string; region: string; rate: number; isDefault: boolean };

function TaxTab() {
  const [behavior, setBehavior] = useState<'inclusive' | 'exclusive'>('inclusive');
  const [regions, setRegions] = useState<TaxRegion[]>([
    { id: 'bd', region: 'Bangladesh (BD)', rate: 15, isDefault: true },
    { id: 'in', region: 'India (IN)', rate: 18, isDefault: false },
    { id: 'default', region: 'Other / Default', rate: 0, isDefault: false },
  ]);
  const [editing, setEditing] = useState<TaxRegion | null>(null);
  const [adding, setAdding] = useState(false);

  function saveRegion(r: TaxRegion) {
    setRegions((prev) => {
      const exists = prev.find((p) => p.id === r.id);
      const updated = exists
        ? prev.map((p) => (p.id === r.id ? r : p))
        : [...prev, r];
      if (r.isDefault) {
        return updated.map((p) => ({ ...p, isDefault: p.id === r.id }));
      }
      return updated;
    });
    setEditing(null);
    setAdding(false);
  }

  function deleteRegion(id: string) {
    setRegions((prev) => prev.filter((r) => r.id !== id));
    setEditing(null);
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <section>
        <h3 className="text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-3">Tax behavior</h3>
        <div className="space-y-2">
          <Radio name="tax" checked={behavior === 'inclusive'} onChange={() => setBehavior('inclusive')} label="Tax inclusive (price already includes tax)" />
          <Radio name="tax" checked={behavior === 'exclusive'} onChange={() => setBehavior('exclusive')} label="Tax exclusive (tax added at checkout)" />
        </div>
      </section>

      <section>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-zinc-900 dark:text-zinc-100">Tax rates by region</h3>
          <Button
            variant="ghost"
            size="sm"
            iconLeft={<Plus className="w-3.5 h-3.5" />}
            onClick={() => setAdding(true)}
          >
            Add region
          </Button>
        </div>
        <div className="space-y-2">
          {regions.map((r) => (
            <div key={r.id} className="flex items-center justify-between p-3 rounded-lg border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-950">
              <div>
                <div className="text-sm font-medium text-zinc-950 dark:text-zinc-50 flex items-center gap-2">
                  {r.region}
                  {r.isDefault && <span className="text-[10px] uppercase tracking-wide bg-zinc-950 dark:bg-zinc-50 text-white dark:text-zinc-950 px-1.5 py-px rounded">Default</span>}
                </div>
                <div className="text-xs text-zinc-500 dark:text-zinc-400">{r.rate === 0 ? 'No tax' : `${r.rate}%`}</div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setEditing(r)}>Edit</Button>
            </div>
          ))}
        </div>
      </section>

      {(adding || editing) && (
        <TaxRegionDrawer
          region={editing}
          onClose={() => {
            setEditing(null);
            setAdding(false);
          }}
          onSave={saveRegion}
          onDelete={editing ? () => deleteRegion(editing.id) : undefined}
        />
      )}
    </div>
  );
}

function TaxRegionDrawer({
  region,
  onClose,
  onSave,
  onDelete,
}: {
  region: TaxRegion | null;
  onClose: () => void;
  onSave: (r: TaxRegion) => void;
  onDelete?: () => void;
}) {
  const [name, setName] = useState(region?.region ?? '');
  const [rate, setRate] = useState(region ? String(region.rate) : '');
  const [isDefault, setIsDefault] = useState(region?.isDefault ?? false);

  function save() {
    if (!name.trim()) return;
    onSave({
      id: region?.id ?? `r_${Date.now()}`,
      region: name.trim(),
      rate: Number(rate) || 0,
      isDefault,
    });
  }

  return (
    <Drawer
      open={true}
      onClose={onClose}
      title={region ? `Edit — ${region.region}` : 'Add tax region'}
      width="md"
      footer={
        <>
          {onDelete && (
            <Button variant="destructive" onClick={onDelete} className="mr-auto">
              Delete
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={!name.trim()}>
            {region ? 'Save' : 'Add region'}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <Input
          label="Region name"
          required
          placeholder="e.g. Singapore (SG)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          hint="ISO country/region label is fine."
        />
        <Input
          label="Tax rate"
          type="number"
          min={0}
          max={100}
          step={0.01}
          value={rate}
          onChange={(e) => setRate(e.target.value)}
          suffix={<span className="text-sm">%</span>}
          hint="0 = no tax for this region."
        />
        <label className="flex items-center gap-2.5 text-sm text-zinc-700 dark:text-zinc-200 cursor-pointer">
          <input
            type="checkbox"
            checked={isDefault}
            onChange={(e) => setIsDefault(e.target.checked)}
            className="rounded border-zinc-300 dark:border-zinc-600 text-brand focus:ring-brand"
          />
          Use as default for unmatched regions
        </label>
      </div>
    </Drawer>
  );
}

function Radio({
  name,
  checked,
  onChange,
  label,
}: {
  name: string;
  checked: boolean;
  onChange: () => void;
  label: string;
}) {
  return (
    <label className="flex items-center gap-2.5 text-sm text-zinc-700 dark:text-zinc-200 cursor-pointer">
      <input type="radio" name={name} checked={checked} onChange={onChange} className="text-brand focus:ring-brand" />
      {label}
    </label>
  );
}

function Check({ label, defaultChecked }: { label: string; defaultChecked?: boolean }) {
  return (
    <label className="flex items-center gap-2.5 text-sm text-zinc-700 dark:text-zinc-200 cursor-pointer">
      <input type="checkbox" defaultChecked={defaultChecked} className="rounded border-zinc-300 dark:border-zinc-600 text-brand focus:ring-brand" />
      {label}
    </label>
  );
}
